// src/App.js
import React, { useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import AuthProvider from "context/AuthContext";
import NotFound from "pages/NotFound";
import Dashboard from "pages/Dashboard";
import Login from "pages/Login";
import ProtectedRoute from "utils/ProtectedRoute";
import Users from "pages/User";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastContainer } from "react-toastify";
import Navbar from "components/ui/NavBar";
import { useJsApiLoader } from "@react-google-maps/api";
import MarketingPrice from "pages/MarketingPrice";
import PricePoint from "pages/PricePoint";
import Prompts from "pages/Prompt";
import PDF from "pages/Pdf";
import Admin from "pages/Admin";
import ChangePassword from "pages/ChangePassword";
import AddPassword from "pages/AddPassword";
import ForgotPassword from "pages/ForgotPassword";
import Settings from "pages/Settings";
import MfaSetup from "pages/MfaSetup";
import Contractors from "pages/Contractor";
import Suburb from "pages/Suburb";
import Events from "pages/Events";
import AuthRedirect from "utils/AuthRedirect";
import SmsLogs from "pages/SmsLogs";
import Edits from "pages/Edits";
import LogicalPrice from "pages/LogicalPrice";
import UploadMarketing from "pages/UploadMarketing";
import Videos from "pages/Videos";
import SocialPost from "pages/SocialPost";
import AiCall from "pages/AiCall";
import Prints from "pages/Print";
import AiCompliance from "pages/AiCompliance";
import CommsTagVendor from "pages/CommsTagVendor";
import CommsTemplateVendor from "pages/CommsTemplateVendor";
import CampaignFaq from "pages/CampaignFaq";
import CommsPropVendor from "pages/CommsPropVendor";
import BuyerCommunication from "pages/BuyerCommunication";
import ChatCommunication from "pages/ChatCommunication";
import CommsTemplateBuyer from "pages/CommsTemplateBuyer";
import CommunityTriggerOptions from "pages/CommunityTriggerOptions";
import CommunityTriggers from "pages/CommunityTriggers";
import TrainMeLibrary from "pages/TrainMeLibrary";
import AddAlert from "pages/AddAlert";
import ContactAlerts from "pages/ContactAlerts";
import Subscriptions from "pages/Subscriptions";
import RentalPrompts from "pages/RentalPrompt";
import SubscriptionPlans from "pages/Products";
import Products from "pages/Products";
import PackageRules from "pages/PackageRules";
import Packages from "pages/Packages";
import ServicesList from "pages/ServicesList";
import LegalDocuments from "pages/LegalDocuments";
import Company from "pages/Company";
import Organization from "pages/Organization";
import Network from "pages/Network";
import Division from "pages/Division";
import RoleGrant from "pages/RoleGrant";
import TemplateLibrary from "pages/TemplateLibrary";
import Office from "pages/Office";
import UserActivity from "pages/UserActivity";
import CampaignEvents from "pages/CampaignEvents";
import AdminOverrides from "pages/AdminOverrides";
import AddressChange from "pages/AddressChange";
import FinancialDashboard from "pages/Financials";
import Shop from "pages/Shop";
import SmsTemplates from "pages/SmsTemplate";
import PageVisits from "pages/PageVisits";
import Waitlist from "pages/Waitlist";
import CompanyOrder from "pages/CompanyOrder";
import OpenHomeMusic from "pages/OpenHomeMusic";
import EventType from "pages/EventType";
import ContractorCompany from "pages/ContractorCompany";
import BuyerCampaignManagement from "pages/BuyerCampaignManagement";
import GeminiValidationLogs from "pages/GeminiValidationLogs";
import AiUsageLogs from "pages/AiUsageLogs";
import Pilot from "pages/Pilot";
import PilotConversations from "pages/PilotConversations";
import PilotKnowledge from "pages/PilotKnowledge";
import EmailDeliveryLogs from "pages/EmailDeliveryLogs";
import FinancialSummary from "pages/FinancialSummary";
import Reports from "pages/Reports";
import Order from "pages/Order";
import Aml from "pages/Aml";
import CampaignFlow from "pages/CampaignFlow";
import RegistrySearchUsage from "pages/RegistrySearchUsage";
import AgencyParty from "pages/AgencyParty";
import { ModalProvider } from "@beleef/ui";
import { EditorShotProvider } from "@beleef/editor-shot";
import { showToast as editorShotToast } from "components/ui/toast";
import DepositAuditReport from "pages/Deposit";
import ProductAnnouncements from "pages/ProductAnnouncements";

const libraries = ["places"]; // Removed "maps" as it's invalid
const queryClient = new QueryClient();

const App = () => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_MAPS_KEY, // Ensure this is correctly set
    libraries, // Corrected libraries
    version: "weekly",
  });

  if (loadError) {
    return <div>Error loading Google Maps. Please try again later.</div>;
  }

  if (!isLoaded) {
    return <div></div>; // You can replace this with a loader/spinner component
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <EditorShotProvider value={{ showToast: editorShotToast }}>
        <ModalProvider>
          <ToastContainer />
          <Routes>
            <Route path="*" element={<NotFound />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/login"
              element={
                <AuthRedirect>
                  <Login />
                </AuthRedirect>
              }
            />
            <Route
              path="/product-announcements"
              element={
                <ProtectedRoute>
                  <ProductAnnouncements />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-activity"
              element={
                <ProtectedRoute>
                  <UserActivity />
                </ProtectedRoute>
              }
            />

            <Route
              path="/company"
              element={
                <ProtectedRoute>
                  <Company />
                </ProtectedRoute>
              }
            />

            <Route
              path="/organization"
              element={
                <ProtectedRoute>
                  <Organization />
                </ProtectedRoute>
              }
            />

            <Route
              path="/network"
              element={
                <ProtectedRoute>
                  <Network />
                </ProtectedRoute>
              }
            />

            <Route
              path="/division"
              element={
                <ProtectedRoute>
                  <Division />
                </ProtectedRoute>
              }
            />

            <Route
              path="/role-grant"
              element={
                <ProtectedRoute>
                  <RoleGrant />
                </ProtectedRoute>
              }
            />

            <Route
              path="/template-library"
              element={
                <ProtectedRoute>
                  <TemplateLibrary />
                </ProtectedRoute>
              }
            />

            <Route
              path="/office"
              element={
                <ProtectedRoute>
                  <Office />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin-overrides"
              element={
                <ProtectedRoute>
                  <AdminOverrides />
                </ProtectedRoute>
              }
            />

            <Route
              path="/address-change"
              element={
                <ProtectedRoute>
                  <AddressChange />
                </ProtectedRoute>
              }
            />

            <Route
              path="/videos"
              element={
                <ProtectedRoute>
                  <Videos />
                </ProtectedRoute>
              }
            />
            <Route
              path="/train-me-library"
              element={
                <ProtectedRoute>
                  <TrainMeLibrary />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-call"
              element={
                <ProtectedRoute>
                  <AiCall />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-compliance"
              element={
                <ProtectedRoute>
                  <AiCompliance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products"
              element={
                <ProtectedRoute>
                  <Products />
                </ProtectedRoute>
              }
            />
            <Route
              path="/package-rules"
              element={
                <ProtectedRoute>
                  <PackageRules />
                </ProtectedRoute>
              }
            />
            <Route
              path="/packages"
              element={
                <ProtectedRoute>
                  <Packages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sms-templates"
              element={
                <ProtectedRoute>
                  <SmsTemplates />
                </ProtectedRoute>
              }
            />

            <Route
              path="/page-visits"
              element={
                <ProtectedRoute>
                  <PageVisits />
                </ProtectedRoute>
              }
            />

            <Route
              path="/event-types"
              element={
                <ProtectedRoute>
                  <EventType />
                </ProtectedRoute>
              }
            />

            <Route
              path="/contractor-company"
              element={
                <ProtectedRoute>
                  <ContractorCompany />
                </ProtectedRoute>
              }
            />

            <Route
              path="/waitlist"
              element={
                <ProtectedRoute>
                  <Waitlist />
                </ProtectedRoute>
              }
            />

            <Route
              path="/legal-documents"
              element={
                <ProtectedRoute>
                  <LegalDocuments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/comms-property-vendor"
              element={
                <ProtectedRoute>
                  <CommsPropVendor />
                </ProtectedRoute>
              }
            />

            <Route
              path="/pricepoint"
              element={
                <ProtectedRoute>
                  <PricePoint />
                </ProtectedRoute>
              }
            />
            <Route
              path="/prompts"
              element={
                <ProtectedRoute>
                  <Prompts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rental-prompts"
              element={
                <ProtectedRoute>
                  <RentalPrompts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/campaignFaq"
              element={
                <ProtectedRoute>
                  <CampaignFaq />
                </ProtectedRoute>
              }
            />
            <Route
              path="/shop"
              element={
                <ProtectedRoute>
                  <Shop />
                </ProtectedRoute>
              }
            />
            <Route
              path="/socialPost"
              element={
                <ProtectedRoute>
                  <SocialPost />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscriptions"
              element={
                <ProtectedRoute>
                  <Subscriptions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/buyerCampaignManagement"
              element={
                <ProtectedRoute>
                  <BuyerCampaignManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/services-list"
              element={
                <ProtectedRoute>
                  <ServicesList />
                </ProtectedRoute>
              }
            />

            <Route
              path="/financial-dashboard"
              element={
                <ProtectedRoute>
                  <FinancialDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/campaign-events"
              element={
                <ProtectedRoute>
                  <CampaignEvents />
                </ProtectedRoute>
              }
            />

            <Route
              path="/event-types"
              element={
                <ProtectedRoute>
                  <EventType />
                </ProtectedRoute>
              }
            />

            <Route
              path="/contractors"
              element={
                <ProtectedRoute>
                  <Contractors />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <Admin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />
            <Route path="/password" element={<AddPassword />} />
            <Route
              path="/forgot-password"
              element={
                <AuthRedirect>
                  <ForgotPassword />
                </AuthRedirect>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            {/* Deliberately NOT inside ProtectedRoute: that would bounce
                straight back here, since the whole point is that the session
                exists but isn't yet allowed anywhere else. MfaSetup does its
                own auth check. */}
            <Route path="/mfa-setup" element={<MfaSetup />} />
            <Route
              path="/events"
              element={
                <ProtectedRoute>
                  <Events />
                </ProtectedRoute>
              }
            />

            <Route
              path="/edits"
              element={
                <ProtectedRoute>
                  <Edits />
                </ProtectedRoute>
              }
            />

            <Route
              path="/edits/:propertyId"
              element={
                <ProtectedRoute>
                  <UploadMarketing />
                </ProtectedRoute>
              }
            />

            {/* One contractor job, still inside UploadMarketing's tab shell so
                the Editor / Admin Extras tabs stay one click away. */}
            <Route
              path="/edits/:propertyId/contractor/:eventId"
              element={
                <ProtectedRoute>
                  <UploadMarketing />
                </ProtectedRoute>
              }
            />

            <Route
              path="/commslog-vendor-tags"
              element={
                <ProtectedRoute>
                  <CommsTagVendor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/buyer-commslog"
              element={
                <ProtectedRoute>
                  <BuyerCommunication />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chat-commslog"
              element={
                <ProtectedRoute>
                  <ChatCommunication />
                </ProtectedRoute>
              }
            />
            <Route
              path="/commslog-vendor-templates"
              element={
                <ProtectedRoute>
                  <CommsTemplateVendor />
                </ProtectedRoute>
              }
            />

            <Route
              path="/commslog-buyer-templates"
              element={
                <ProtectedRoute>
                  <CommsTemplateBuyer />
                </ProtectedRoute>
              }
            />

            <Route
              path="/sms-logs"
              element={
                <ProtectedRoute>
                  <SmsLogs />
                </ProtectedRoute>
              }
            />

            <Route
              path="/logical-price"
              element={
                <ProtectedRoute>
                  <LogicalPrice />
                </ProtectedRoute>
              }
            />

            <Route
              path="/aml"
              element={
                <ProtectedRoute>
                  <Aml />
                </ProtectedRoute>
              }
            />

            <Route
              path="/campaign-flow"
              element={
                <ProtectedRoute>
                  <CampaignFlow />
                </ProtectedRoute>
              }
            />

            <Route
              path="/agency-party"
              element={
                <ProtectedRoute>
                  <AgencyParty />
                </ProtectedRoute>
              }
            />

            <Route path="/pdf" element={<PDF />} />

            <Route
              path="/add-alert"
              element={
                <ProtectedRoute>
                  <AddAlert />
                </ProtectedRoute>
              }
            />

            <Route
              path="/deposit"
              element={
                <ProtectedRoute>
                  <DepositAuditReport />
                </ProtectedRoute>
              }
            />

            <Route
              path="/company-orders"
              element={
                <ProtectedRoute>
                  <CompanyOrder />
                </ProtectedRoute>
              }
            />

            <Route
              path="/orders"
              element={
                <ProtectedRoute>
                  <Order />
                </ProtectedRoute>
              }
            />

            <Route
              path="/contact-alerts"
              element={
                <ProtectedRoute>
                  <ContactAlerts />
                </ProtectedRoute>
              }
            />

            <Route
              path="/marketing-price"
              element={
                <ProtectedRoute>
                  <MarketingPrice />
                </ProtectedRoute>
              }
            />

            <Route
              path="/community-triggers"
              element={
                <ProtectedRoute>
                  <CommunityTriggers />
                </ProtectedRoute>
              }
            />

            <Route
              path="/community-trigger-options"
              element={
                <ProtectedRoute>
                  <CommunityTriggerOptions />
                </ProtectedRoute>
              }
            />

            <Route
              path="/suburb"
              element={
                <ProtectedRoute>
                  <Suburb />
                </ProtectedRoute>
              }
            />

            <Route
              path="/prints"
              element={
                <ProtectedRoute>
                  <Prints />
                </ProtectedRoute>
              }
            />

            <Route
              path="/openHomeMusic"
              element={
                <ProtectedRoute>
                  <OpenHomeMusic />
                </ProtectedRoute>
              }
            />

            <Route
              path="/gemini-validation-logs"
              element={
                <ProtectedRoute>
                  <GeminiValidationLogs />
                </ProtectedRoute>
              }
            />

            <Route
              path="/ai-usage-logs"
              element={
                <ProtectedRoute>
                  <AiUsageLogs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pilot-chat"
              element={
                <ProtectedRoute>
                  <Pilot />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pilot-conversations"
              element={
                <ProtectedRoute>
                  <PilotConversations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pilot-knowledge"
              element={
                <ProtectedRoute>
                  <PilotKnowledge />
                </ProtectedRoute>
              }
            />
            <Route
              path="/email-logs"
              element={
                <ProtectedRoute>
                  <EmailDeliveryLogs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/financial-summary"
              element={
                <ProtectedRoute>
                  <FinancialSummary />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute>
                  <Reports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/registry-search-usage"
              element={
                <ProtectedRoute>
                  <RegistrySearchUsage />
                </ProtectedRoute>
              }
            />
          </Routes>
        </ModalProvider>
        </EditorShotProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
